import React, { useEffect, useState } from "react";
import {
  Alert, Avatar, Box, Button, Card, CardActionArea, CardContent, Checkbox, CircularProgress,
  Container, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Paper, Stack, TextField, Typography
} from "@mui/material";
import { ArrowBack, Delete, Download, Email, Group, History, MeetingRoom, Refresh, Share } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import api from "lib/api/api";
import AusbildungNavbar from "./AusbildungNavbar";

export default function AusbildungRoomsPage() {
  const navigate = useNavigate();
  const { categoryKey } = useParams();
  const [rooms, setRooms] = useState([]);
  const [photos, setPhotos] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareEmail, setShareEmail] = useState({ recipient: "", subject: "", body: "" });
  const [shareMessage, setShareMessage] = useState("");
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyNotice, setHistoryNotice] = useState("");
  const [selectedHistory, setSelectedHistory] = useState([]);

  const loadRooms = async () => {
    setRefreshing(true);
    setError("");
    try {
      const { data } = await api.get("/ausbildung-management/rooms");
      setRooms(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.response?.data?.message || "Could not load Ausbildung rooms.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { loadRooms(); }, []);

  useEffect(() => {
    let active = true;
    const applicants = rooms.flatMap(room => room.applicants || []).filter(person => person.photoAvailable);
    Promise.all(applicants.map(async person => {
      try {
        const { data } = await api.get(`/ausbildung-management/submissions/${person.id}/cv/photo`, { responseType: "blob" });
        return [person.id, URL.createObjectURL(data)];
      } catch { return [person.id, ""]; }
    })).then(results => {
      if (active) setPhotos(Object.fromEntries(results.filter(([, url]) => Boolean(url))));
      else results.forEach(([, url]) => url && URL.revokeObjectURL(url));
    });
    return () => { active = false; };
  }, [rooms]);

  useEffect(() => () => Object.values(photos).forEach(URL.revokeObjectURL), [photos]);

  const slug = value => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const roomKey = room => room.categoryId == null ? slug(room.categoryName) : `${room.categoryId}-${slug(room.categoryName)}`;
  const activeRoom = categoryKey ? rooms.find(room => roomKey(room) === categoryKey) : null;

  const createSharePage = async room => {
    if (!room?.categoryId) return;
    setShareBusy(true);
    setShareMessage("");
    try {
      const { data } = await api.post(`/ausbildung-management/categories/${room.categoryId}/share`);
      setShareTarget(data);
      setShareEmail({ recipient: data.recipientEmail || "", subject: data.emailSubject || "", body: data.emailBody || "" });
    } catch (e) {
      setError(e.response?.data?.message || "Could not create the protected room page.");
    } finally { setShareBusy(false); }
  };

  const downloadCv = async person => {
    try {
      const { data } = await api.get(`/ausbildung-management/submissions/${person.id}/cv/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `ALGermanist-Lebenslauf-${(person.name || "Applicant").replace(/[^a-z0-9_-]/gi, "-")}.pdf`;
      document.body.appendChild(anchor); anchor.click(); anchor.remove(); URL.revokeObjectURL(url);
    } catch (e) { setError(e.response?.data?.message || "Could not download the generated Lebenslauf."); }
  };

  const sendShareEmail = async () => {
    if (!shareTarget || !shareEmail.recipient.trim()) return;
    setShareBusy(true);
    setShareMessage("");
    try {
      const token = shareTarget.url.split("/").pop();
      await api.post(`/ausbildung-management/categories/${shareTarget.categoryId}/share/send`, {
        token, password: shareTarget.password, ...shareEmail
      });
      setShareMessage("Access email sent successfully.");
      if (historyTarget?.categoryId === shareTarget.categoryId) loadHistory(shareTarget.categoryId, historyTarget.categoryName);
    } catch (e) {
      setShareMessage(e.response?.data?.message || "Could not send the access email.");
    } finally { setShareBusy(false); }
  };

  const loadHistory = async (categoryId, categoryName) => {
    setHistoryTarget({ categoryId, categoryName });
    setHistoryBusy(true);
    setHistoryError("");
    setHistoryNotice("");
    setSelectedHistory([]);
    try {
      const { data } = await api.get(`/ausbildung-management/categories/${categoryId}/share/history`);
      setHistoryRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setHistoryError(e.response?.data?.message || "Could not load room email history.");
      setHistoryRows([]);
    } finally { setHistoryBusy(false); }
  };

  const deleteHistoryEntry = async entry => {
    try {
      await api.delete(`/ausbildung-management/categories/${historyTarget.categoryId}/share/history/${entry.id}`);
      setHistoryRows(rows => rows.filter(row => row.id !== entry.id));
      setSelectedHistory(ids => ids.filter(id => id !== entry.id));
      setHistoryNotice("History entry deleted.");
    } catch (e) { setHistoryError(e.response?.data?.message || "Could not delete this history entry."); }
  };

  const deleteSelectedHistory = async () => {
    if (!historyTarget || selectedHistory.length === 0) return;
    setHistoryBusy(true);
    try {
      await api.delete(`/ausbildung-management/categories/${historyTarget.categoryId}/share/history`, { data: { ids: selectedHistory } });
      setHistoryRows(rows => rows.filter(row => !selectedHistory.includes(row.id)));
      setSelectedHistory([]);
      setHistoryNotice("Selected history entries deleted.");
    } catch (e) { setHistoryError(e.response?.data?.message || "Could not delete the selected history entries."); }
    finally { setHistoryBusy(false); }
  };

  const fillEmail = text => (text || "").replaceAll("{{category}}", shareTarget?.categoryName || "")
    .replaceAll("{{url}}", shareTarget?.url || "").replaceAll("{{password}}", shareTarget?.password || "");

  return <>
    <AusbildungNavbar />
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={900}>Ausbildung Rooms</Typography>
          <Typography color="text.secondary">Approved applicants grouped by Ausbildung category and CV step.</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={loadRooms} disabled={refreshing}>{refreshing ? "Refreshing…" : "Refresh"}</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {categoryKey && !loading && !activeRoom && <Alert severity="warning" sx={{ mb: 2 }}>This Ausbildung category could not be found.</Alert>}
      {loading ? <Box sx={{ py: 10, display: "grid", placeItems: "center" }}><CircularProgress /></Box> : !rooms.length ?
        <Alert severity="info">No Ausbildung categories are available.</Alert> :
        activeRoom ? <>
          <Button startIcon={<ArrowBack />} onClick={() => navigate("/dashboard/ausbildung/rooms")} sx={{ mb: 2 }}>All rooms</Button>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <MeetingRoom color="primary" />
            <Typography variant="h5" fontWeight={900}>{activeRoom.categoryName}</Typography>
            <Typography color="text.secondary">· {activeRoom.applicants?.length || 0} approved applicants</Typography>
          </Stack>
          <Button variant="outlined" startIcon={<Share />} onClick={() => createSharePage(activeRoom)} disabled={shareBusy} sx={{ mb: 2 }}>Generate protected room page</Button>
          <Button variant="outlined" startIcon={<History />} onClick={() => loadHistory(activeRoom.categoryId, activeRoom.categoryName)} sx={{ mb: 2, ml: 1 }}>Email history</Button>
          {!activeRoom.applicants?.length ? <Alert severity="info">No approved applicants in this room yet.</Alert> :
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(3,minmax(0,1fr))", md: "repeat(4,minmax(0,1fr))" }, gap: 2 }}>
              {activeRoom.applicants.map(person => <Card key={person.id} variant="outlined" sx={{ borderRadius: 4, overflow: "hidden", borderColor: "#e3e9f2", "&:hover": { borderColor: "#7c3aed", boxShadow: "0 8px 22px rgba(76,29,149,.15)" } }}>
                <CardActionArea onClick={() => navigate(`/dashboard/ausbildung/submissions?applicationId=${person.id}&editCv=1`)} sx={{ p: 2, minHeight: 164, display: "flex", flexDirection: "column", gap: 1.25 }}>
                  <Avatar src={photos[person.id] || undefined} sx={{ width: 88, height: 88, bgcolor: person.gender === "female" ? "#c3547e" : person.gender === "male" ? "#377099" : "#4a7569", fontSize: 30, fontWeight: 800 }}>
                    {(person.name || "?").trim().charAt(0).toLocaleUpperCase()}
                  </Avatar>
                  <Typography fontWeight={800} textAlign="center" noWrap sx={{ maxWidth: "100%" }}>{person.name || "Applicant"}</Typography>
                </CardActionArea>
                <Box sx={{ px: 2, pb: 2, textAlign: "center" }}>{person.cvAvailable ? <Button size="small" startIcon={<Download />} onClick={() => downloadCv(person)}>Download Lebenslauf</Button> : <Typography variant="body2" color="warning.dark" fontWeight={700}>Lebenslauf in progress</Typography>}</Box>
              </Card>)}
            </Box>}
        </> : <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" }, gap: 2.5 }}>
          {rooms.map((room, index) => {
            const colors = [
              ["#4c1d95", "#7c3aed", "#ede9fe"], ["#075985", "#0284c7", "#e0f2fe"],
              ["#9a3412", "#ea580c", "#ffedd5"], ["#155e35", "#16a34a", "#dcfce7"],
              ["#9f1239", "#e11d48", "#ffe4e6"]
            ][index % 5];
            return <Card key={room.categoryId || "unassigned"} sx={{ overflow: "hidden", borderRadius: 5, border: `1px solid ${colors[1]}55`, boxShadow: `0 14px 38px ${colors[0]}20`, background: "#fff", transition: "transform .18s, box-shadow .18s", "&:hover": { transform: "translateY(-4px)", boxShadow: `0 20px 44px ${colors[0]}30` } }}>
              <CardActionArea onClick={() => navigate(`/dashboard/ausbildung/rooms/${roomKey(room)}`)} aria-label={`Open ${room.categoryName} room`}>
                <Box sx={{ p: 2.5, color: "#fff", background: `linear-gradient(120deg,${colors[0]},${colors[1]})`, minHeight: 138, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box><Typography variant="overline" sx={{ opacity: .82 }}>CATEGORY ROOM</Typography><Typography variant="h5" fontWeight={900}>{room.categoryName}</Typography></Box>
                  <Avatar sx={{ width: 58, height: 58, bgcolor: "rgba(255,255,255,.2)", border: "1px solid rgba(255,255,255,.42)" }}><MeetingRoom /></Avatar>
                </Box>
              </CardActionArea>
              <CardContent sx={{ p: 2.5, bgcolor: colors[2] }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Group color="action" fontSize="small" />
                  <Typography fontWeight={800}>{room.applicants?.length || 0} approved applicants</Typography>
                </Stack>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button fullWidth size="small" variant="outlined" onClick={() => navigate(`/dashboard/ausbildung/rooms/${roomKey(room)}`)}>Open applicants</Button>
                  <Button fullWidth size="small" variant="contained" startIcon={<Share />} disabled={!room.categoryId || shareBusy} onClick={() => createSharePage(room)}>Generate share page</Button>
                  <Button fullWidth size="small" variant="outlined" startIcon={<History />} disabled={!room.categoryId} onClick={() => loadHistory(room.categoryId, room.categoryName)}>Email history</Button>
                </Stack>
              </CardContent>
            </Card>;
          })}
        </Box>}
    </Container>
    <Dialog open={Boolean(shareTarget)} onClose={() => !shareBusy && setShareTarget(null)} maxWidth="md" fullWidth>
      <DialogTitle>Preview protected room page and access email</DialogTitle>
      <DialogContent dividers>
        {shareTarget && <Stack spacing={2} sx={{ pt: .5 }}>
          <Alert severity="info">The generated password is required to open the public page. Generating a replacement share for this category invalidates the previous URL and password.</Alert>
          {shareMessage && <Alert severity={shareMessage.includes("successfully") ? "success" : "error"}>{shareMessage}</Alert>}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField fullWidth label="Public room page" value={shareTarget.url} InputProps={{ readOnly: true }} />
            <TextField label="Generated access password" value={shareTarget.password} InputProps={{ readOnly: true }} />
          </Stack>
          <Button variant="outlined" onClick={() => window.open(shareTarget.url, "_blank", "noopener,noreferrer")}>Preview protected page</Button>
          <Divider />
          <Typography variant="h6" fontWeight={800}>Email details</Typography>
          <TextField fullWidth required type="email" label="Send access email to" value={shareEmail.recipient} onChange={e => setShareEmail(current => ({ ...current, recipient: e.target.value }))} />
          <TextField fullWidth required label="Email subject" value={shareEmail.subject} onChange={e => setShareEmail(current => ({ ...current, subject: e.target.value }))} />
          <TextField fullWidth required multiline minRows={5} label="Email message ({{category}}, {{url}}, {{password}})" value={shareEmail.body} onChange={e => setShareEmail(current => ({ ...current, body: e.target.value }))} />
          <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f7faf9" }}>
            <Typography fontWeight={800} sx={{ mb: 1 }}><Email sx={{ fontSize: 17, verticalAlign: "middle", mr: .5 }} />Email preview</Typography>
            <Typography variant="subtitle2">{fillEmail(shareEmail.subject)}</Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 1 }}>{fillEmail(shareEmail.body)}</Typography>
          </Paper>
        </Stack>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShareTarget(null)} disabled={shareBusy}>Close</Button>
        <Button variant="contained" startIcon={<Email />} onClick={sendShareEmail} disabled={shareBusy || !shareEmail.recipient.trim()}>{shareBusy ? "Working…" : "Send access email"}</Button>
      </DialogActions>
    </Dialog>
    <Dialog open={Boolean(historyTarget)} onClose={() => !historyBusy && setHistoryTarget(null)} maxWidth="md" fullWidth>
      <DialogTitle>Room email history{historyTarget ? ` · ${historyTarget.categoryName}` : ""}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: .5 }}>
          <Alert severity="warning">This history contains access passwords and public links. Share it only with authorized staff.</Alert>
          {historyError && <Alert severity="error">{historyError}</Alert>}
          {historyNotice && <Alert severity="success">{historyNotice}</Alert>}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography color="text.secondary">{historyRows.length} sent {historyRows.length === 1 ? "email" : "emails"}</Typography>
            <Button color="error" startIcon={<Delete />} disabled={historyBusy || selectedHistory.length === 0} onClick={deleteSelectedHistory}>Delete selected</Button>
          </Stack>
          {historyBusy && <Box sx={{ display: "grid", placeItems: "center", py: 3 }}><CircularProgress /></Box>}
          {!historyBusy && historyRows.length === 0 && <Alert severity="info">No room access emails have been sent for this category.</Alert>}
          {historyRows.map(entry => <Paper key={entry.id} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Checkbox checked={selectedHistory.includes(entry.id)} onChange={e => setSelectedHistory(ids => e.target.checked ? [...ids, entry.id] : ids.filter(id => id !== entry.id))} inputProps={{ "aria-label": `Select email to ${entry.recipientEmail}` }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={800}>{entry.recipientEmail}</Typography>
                <Typography variant="caption" color="text.secondary">Sent {entry.sentAt ? new Date(entry.sentAt).toLocaleString() : ""}</Typography>
                <TextField fullWidth size="small" label="Subject" value={entry.emailSubject || ""} InputProps={{ readOnly: true }} sx={{ mt: 1 }} />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1 }}>
                  <TextField fullWidth size="small" label="Page link" value={entry.pageUrl || ""} InputProps={{ readOnly: true }} />
                  <TextField size="small" label="Password sent" value={entry.accessPassword || ""} InputProps={{ readOnly: true }} />
                </Stack>
                <TextField fullWidth size="small" multiline minRows={3} label="Email message sent" value={entry.emailBody || ""} InputProps={{ readOnly: true }} sx={{ mt: 1 }} />
              </Box>
              <IconButton color="error" aria-label={`Delete email history for ${entry.recipientEmail}`} onClick={() => deleteHistoryEntry(entry)}><Delete /></IconButton>
            </Stack>
          </Paper>)}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={() => setHistoryTarget(null)} disabled={historyBusy}>Close</Button></DialogActions>
    </Dialog>
  </>;
}
